
import React, { useState, useRef, useCallback } from 'react';
import { Coordinates, MapBounds, CampingSpot } from '../types';
import { analyzeSpot } from '../services/geminiService';
import { reverseGeocode } from '../services/geocodingService';
import { upsertSpotsToFirebase, markSpotAsInvalid, deepAnalyzeServer, enrichSpotFishing } from '../services/firebaseService';
import { fetchRoadAndBuildingForPoint, fetchNearestBuildingDist, findNearestRoadForPoint } from '../services/overpassService';
import { enrichSpotWithGeoData, isInnmark } from '../services/geoValidationService';
import { BRAND } from '../constants/mapColors';

export interface AnalysisStats {
  checked: number;
  removedHouse: number;
  removedRoad: number;
  removedAI: number;
  kept: number;
}

// Helper to yield control back to main thread to prevent UI freezing
const yieldToMain = (ms: number = 50) => new Promise(resolve => setTimeout(resolve, ms));

export const useAnalysisManager = (
  currentViewBounds: MapBounds | null,
  spots: CampingSpot[],
  setSpots: React.Dispatch<React.SetStateAction<CampingSpot[]>>,
  scanArea: (signal?: AbortSignal) => Promise<CampingSpot[] | null>,
  setIsLoading: (val: boolean) => void,
  onSelectSpot: (s: CampingSpot) => void,
  activeAnalysisIds: Set<string>,
  setActiveAnalysisIds: React.Dispatch<React.SetStateAction<Set<string>>>,
  addToast?: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void
) => {
  const [analysisProgress, setAnalysisProgress] = useState({ current: 0, total: 0 });
  const [pendingAnalysisCoords, setPendingAnalysisCoords] = useState<Coordinates | null>(null);
  const [isOverlayMinimized, setIsOverlayMinimized] = useState(false);
  const [isManualAnalyzing, setIsManualAnalyzing] = useState(false);
  const [isViewportAnalyzing, setIsViewportAnalyzing] = useState(false);
  const [queueLength, setQueueLength] = useState(0);

  const abortControllerRef = useRef<AbortController | null>(null);
  const viewportAbortRef = useRef<AbortController | null>(null);
  const analysisQueueRef = useRef<Coordinates[]>([]);
  const isProcessingRef = useRef(false);

  const handleCancelScan = useCallback(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    if (viewportAbortRef.current) viewportAbortRef.current.abort();
    setIsLoading(false);
    setIsViewportAnalyzing(false);
    setIsManualAnalyzing(false);
    setActiveAnalysisIds(new Set());
    setAnalysisProgress({ current: 0, total: 0 });
    setIsOverlayMinimized(false);
  }, [setIsLoading, setActiveAnalysisIds]);

  const handleMinimize = useCallback(() => {
    setIsOverlayMinimized(true);
  }, []);

  // --- Client-side fallback for deep analysis (original 2-at-a-time logic) ---
  const runDeepAnalysisClientFallback = async (targetSpots: CampingSpot[], signal: AbortSignal) => {
    const CONCURRENCY_LIMIT = 2;
    let processedCount = 0;

    for (let i = 0; i < targetSpots.length; i += CONCURRENCY_LIMIT) {
      if (signal.aborted) break;
      const chunk = targetSpots.slice(i, i + CONCURRENCY_LIMIT);

      setActiveAnalysisIds(prev => {
        const next = new Set(prev);
        chunk.forEach(s => next.add(s.id));
        return next;
      });

      const validUpdates: CampingSpot[] = [];
      const invalidIds: string[] = [];

      await Promise.all(chunk.map(async (spot) => {
        try {
          let enrichedSpot = { ...spot };
          if (spot.isInnmark === undefined) {
            try { enrichedSpot.isInnmark = await isInnmark(spot.location.latitude, spot.location.longitude); } catch { }
          }
          if (enrichedSpot.isInnmark === true) {
            await markSpotAsInvalid(spot.id, 'Innmark (dyrket mark) – friluftsloven forbyr camping');
            invalidIds.push(spot.id);
            return;
          }
          const ai = await analyzeSpot(enrichedSpot, false, 0, signal);
          if (ai && !signal.aborted) {
            const isBad = ai.score < 3 || ai.anbefaling === 'FORBUDT';
            if (isBad) {
              await markSpotAsInvalid(spot.id, `AI-vask: ${ai.vurdering}`);
              invalidIds.push(spot.id);
            } else {
              const updated = { ...enrichedSpot, aiResult: ai, score: ai.score, isInvalid: false, ...(Number.isFinite(ai.elevation_moh) && !spot.elevation ? { elevation: Math.round(ai.elevation_moh) } : {}) };
              validUpdates.push(updated);
            }
          }
        } catch { console.warn("Analyse feilet:", spot.id); }
        finally { processedCount++; }
      }));

      if (validUpdates.length > 0 || invalidIds.length > 0) {
        setSpots(prev => {
          let next = prev.filter(s => !invalidIds.includes(s.id));
          if (validUpdates.length > 0) {
            const updateMap = new Map(validUpdates.map(s => [s.id, s]));
            next = next.map(s => updateMap.has(s.id) ? updateMap.get(s.id)! : s);
          }
          return next;
        });
        if (validUpdates.length > 0) {
          await upsertSpotsToFirebase(validUpdates);
          // Fire-and-forget: enrich spots with fishing scores
          validUpdates.forEach(s => {
            enrichSpotFishing(s.location.latitude, s.location.longitude, s.id).catch(() => {});
          });
        }
      }

      setActiveAnalysisIds(prev => {
        const next = new Set(prev);
        chunk.forEach(s => next.delete(s.id));
        return next;
      });

      setAnalysisProgress(prev => ({ ...prev, current: processedCount }));
      await yieldToMain(100);
    }
  };

  // --- Main deep analysis: tries server batch first, falls back to client ---
  const runDeepAnalysisOnSpots = async (targetSpots: CampingSpot[], signal: AbortSignal) => {
    if (targetSpots.length === 0) return;

    setIsViewportAnalyzing(true);
    setAnalysisProgress({ current: 0, total: targetSpots.length });

    // Mark all spots as active for UI
    setActiveAnalysisIds(prev => {
      const next = new Set(prev);
      targetSpots.forEach(s => next.add(s.id));
      return next;
    });

    try {
      // Try server-side batch analysis (all AI runs on Cloud Function)
      const spotIds = targetSpots.map(s => s.id);
      const result = await deepAnalyzeServer(spotIds);

      if (signal.aborted) return;

      // Apply results to local state
      const removedIds = new Set(result.results.filter(r => r.status === 'removed').map(r => r.id));
      const keptResults = result.results.filter(r => r.status === 'kept' && r.aiResult);

      setSpots(prev => {
        let next = prev.filter(s => !removedIds.has(s.id));
        if (keptResults.length > 0) {
          const updateMap = new Map(keptResults.map(r => [r.id, r]));
          next = next.map(s => {
            const serverResult = updateMap.get(s.id);
            if (serverResult?.aiResult) {
              return {
                ...s,
                aiResult: serverResult.aiResult,
                score: serverResult.score ?? s.score,
                isInvalid: false,
                ...(Number.isFinite(serverResult.aiResult.elevation_moh) && !s.elevation
                  ? { elevation: Math.round(serverResult.aiResult.elevation_moh) } : {})
              };
            }
            return s;
          });
        }
        return next;
      });

      setAnalysisProgress({ current: targetSpots.length, total: targetSpots.length });
      console.log(`[deepAnalysis] Server batch complete: ${result.kept} kept, ${result.removed} removed`);

    } catch (serverErr: any) {
      if (signal.aborted) return;
      console.warn('[deepAnalysis] Server batch failed, falling back to client-side:', serverErr.message);
      await runDeepAnalysisClientFallback(targetSpots, signal);
    }

    // Cleanup
    setActiveAnalysisIds(new Set());
    setIsViewportAnalyzing(false);
    setAnalysisProgress({ current: 0, total: 0 });
    setIsOverlayMinimized(false);
  };

  const handleScanWithBg = async () => {
    if (!currentViewBounds) return;
    handleCancelScan();
    
    const ctrl = new AbortController();
    abortControllerRef.current = ctrl;
    const newSpots = await scanArea(ctrl.signal);
    
    if (newSpots && newSpots.length > 0 && !ctrl.signal.aborted) {
      const algorithmicSpots = newSpots.filter(s => s.spotType === 'algorithmic' && !s.aiResult);
      if (algorithmicSpots.length > 0) {
        // Fire-and-forget: never let a rejected persistence write (e.g. a
        // permission-denied for non-admins) escape as an unhandled rejection.
        runDeepAnalysisOnSpots(algorithmicSpots, ctrl.signal).catch(err => {
          console.warn('[deepAnalysis] background analysis failed:', err);
        });
      }
    }
  };

  const handleDeepAnalyzeViewport = async () => {
    if (!currentViewBounds || isViewportAnalyzing) return;
    const pendingSpots = spots.filter(s => 
      s.spotType === 'algorithmic' && !s.isInvalid &&
      s.location.latitude >= currentViewBounds.latMin && s.location.latitude <= currentViewBounds.latMax &&
      s.location.longitude >= currentViewBounds.lonMin && s.location.longitude <= currentViewBounds.lonMax &&
      !s.aiResult
    );
    if (pendingSpots.length === 0) return;
    const ctrl = new AbortController();
    viewportAbortRef.current = ctrl;
    await runDeepAnalysisOnSpots(pendingSpots, ctrl.signal);
  };

  /**
   * STRICT RE-EVALUATION LOGIC
   */
  const handleReevaluateViewport = async (): Promise<AnalysisStats | null> => {
    if (!currentViewBounds || isViewportAnalyzing) return null;

    const candidates = spots.filter(s => 
      s.spotType === 'algorithmic' && 
      s.sourceLevel !== 4 && 
      !s.isInvalid &&
      s.location.latitude >= currentViewBounds.latMin && s.location.latitude <= currentViewBounds.latMax &&
      s.location.longitude >= currentViewBounds.lonMin && s.location.longitude <= currentViewBounds.lonMax
    );

    if (candidates.length === 0) {
        addToast?.("Ingen AI-genererte punkter i dette utsnittet å revurdere.", 'info');
        return null;
    }

    setIsViewportAnalyzing(true);
    setAnalysisProgress({ current: 0, total: candidates.length });
    const ctrl = new AbortController();
    viewportAbortRef.current = ctrl;

    let stats: AnalysisStats = {
        checked: 0,
        removedHouse: 0,
        removedRoad: 0,
        removedAI: 0,
        kept: 0
    };

    let processedCount = 0;
    const CONCURRENCY = 2; 

    for (let i = 0; i < candidates.length; i += CONCURRENCY) {
        if (ctrl.signal.aborted) break;
        const chunk = candidates.slice(i, i + CONCURRENCY);

        setActiveAnalysisIds(prev => {
            const next = new Set(prev);
            chunk.forEach(s => next.add(s.id));
            return next;
        });

        const idsToRemove: string[] = [];
        const validUpdates: CampingSpot[] = [];

        await Promise.all(chunk.map(async (spot) => {
            try {
                stats.checked++;

                // STEP 1: Strict House Check
                const houseDist = await fetchNearestBuildingDist(spot.location);
                if (houseDist < 145) {
                    await markSpotAsInvalid(spot.id, `Revurdering: For nær hus (${houseDist}m)`);
                    idsToRemove.push(spot.id);
                    stats.removedHouse++;
                    return;
                }

                // STEP 1b: Strict Innmark Check
                let spotIsInnmark = spot.isInnmark;
                if (spotIsInnmark === undefined) {
                    try { spotIsInnmark = await isInnmark(spot.location.latitude, spot.location.longitude); } catch { /* keep undefined */ }
                }
                if (spotIsInnmark === true) {
                    await markSpotAsInvalid(spot.id, 'Revurdering: Innmark (dyrket mark) – forbudt');
                    idsToRemove.push(spot.id);
                    stats.removedHouse++; // count with house removals
                    return;
                }

                // STEP 2: Strict Road Check
                const roadInfo = await findNearestRoadForPoint(spot.location);
                const distToRoad = roadInfo?.distance ?? 999;

                const isFacility = spot.features.some(f => ["GAPAHUK", "BÅLPLASS", "PICNIC", "SNUPLASS"].includes(f));
                if (distToRoad > 150 && !isFacility) {
                    await markSpotAsInvalid(spot.id, `Revurdering: For langt fra vei (${Math.round(distToRoad)}m)`);
                    idsToRemove.push(spot.id);
                    stats.removedRoad++;
                    return;
                }

                // STEP 3: AI Re-analysis (with innmark data)
                const ai = await analyzeSpot({
                    ...spot,
                    nearestHouse: houseDist,
                    isInnmark: spotIsInnmark,
                    highway: roadInfo?.highway || spot.highway
                }, false, 0, undefined, true);

                if (ai) {
                    const isBad = ai.score < 4 || ai.anbefaling === 'FORBUDT';
                    if (isBad) {
                        await markSpotAsInvalid(spot.id, `Revurdering AI: ${ai.vurdering} (Score: ${ai.score})`);
                        idsToRemove.push(spot.id);
                        stats.removedAI++;
                    } else {
                        const updated = { ...spot, aiResult: ai, score: ai.score, nearestHouse: houseDist, ...(Number.isFinite(ai.elevation_moh) && !spot.elevation ? { elevation: Math.round(ai.elevation_moh) } : {}) };
                        validUpdates.push(updated);
                        stats.kept++;
                    }
                } else {
                    stats.kept++; // Keep if AI fails (conservative)
                }

            } catch (e) {
                console.warn("Re-eval failed for", spot.id);
            } finally {
                processedCount++;
            }
        }));

        // BATCH UPDATE
        if (idsToRemove.length > 0 || validUpdates.length > 0) {
            setSpots(prev => {
                let next = prev.filter(x => !idsToRemove.includes(x.id));
                if (validUpdates.length > 0) {
                    const updateMap = new Map(validUpdates.map(s => [s.id, s]));
                    next = next.map(s => updateMap.has(s.id) ? updateMap.get(s.id)! : s);
                }
                return next;
            });
            if (validUpdates.length > 0) await upsertSpotsToFirebase(validUpdates);
        }

        // Cleanup Active IDs
        setActiveAnalysisIds(prev => {
            const next = new Set(prev);
            chunk.forEach(s => next.delete(s.id));
            return next;
        });

        setAnalysisProgress(prev => ({ ...prev, current: processedCount }));
        
        // YIELD TO MAIN THREAD
        await yieldToMain(100);
    }

    setIsViewportAnalyzing(false);
    setAnalysisProgress({ current: 0, total: 0 });
    return stats;
  };

  const analyzeOneCoords = async (coords: Coordinates) => {
    const tempId = `manual-${Date.now()}`;

    try {
      // FASE 1: Hent kun det AI-en trenger + innmark-sjekk (parallelt ~1-2s)
      const [{ roadInfo, buildingDist: currentHouseDist }, locationName, innmarkResult] = await Promise.all([
        fetchRoadAndBuildingForPoint(coords),
        reverseGeocode(coords),
        isInnmark(coords.latitude, coords.longitude)
      ]);

      // Gate: Innmark = avbryt umiddelbart
      if (innmarkResult === true) {
        addToast?.("Punktet ligger på innmark (dyrket mark)", 'warning');
        return;
      }

      const isSnu = (roadInfo?.highway === 'turning_circle') || locationName.toUpperCase().includes("SNUPLASS");

      // Bygg minimalt spot-objekt for AI (IKKE lagt til kart/DB ennå)
      const spotForAI: CampingSpot = {
        id: tempId,
        source: 'ai',
        externalId: tempId,
        importVersion: 'ai-manual',
        lastSyncedAt: new Date().toISOString(),
        name: isSnu ? "SNUPLASS" : (locationName || "Valgt punkt"),
        description: "Analyserer punkt...",
        location: coords,
        score: isSnu ? 0 : 5,
        spotType: 'algorithmic',
        sourceLevel: 2,
        suitabilityColor: isSnu ? '#2563eb' : '#57534e',
        features: isSnu ? ["MANUELT", "SNUPLASS"] : ["MANUELT"],
        reasoning: "Brukerstyrt analyse.",
        trafficRisk: "Ukjent",
        buildingProximity: currentHouseDist < 150 ? "FOR NÆR HUS" : "OK",
        nearestHouse: currentHouseDist,
        highway: roadInfo?.highway || "Ukjent",
        surfaceType: (roadInfo?.surface as any) || 'Ukjent',
        isPublicAccess: true,
        isInnmark: false,
        isDriveable: roadInfo?.isDriveable ?? true,
        nearestDriveableHighway: roadInfo?.nearestDriveableHighway || null,
        nearestDriveableDist: roadInfo?.nearestDriveableDist || null,
        accessRoute: roadInfo?.geometry,
        legal: { isUtmark: true, inProtectedArea: false, hasBarrier: false, distToFarmland: 999, nearMajorRoad: false }
      };

      // FASE 2: AI-analyse (~3-8s) — spot er IKKE på kartet ennå
      const ai = await analyzeSpot(spotForAI, true);

      if (!ai) {
        addToast?.("Analyse feilet. Prøv igjen.", 'error');
        return;
      }

      // FASE 3: Avgjørelse — nærliggende bebyggelse diskvalifiserer ikke, men senker score til 4
      const distToRoad = roadInfo?.distance ?? 999;
      const isFacility = spotForAI.features.some(f =>
        ["GAPAHUK", "BÅLPLASS", "PICNIC", "SNUPLASS"].includes(f));
      // roadInfo === null means Overpass was unavailable — don't falsely reject based on missing data
      const isNoRoad = roadInfo !== null && distToRoad > 150 && !isFacility;
      const tooCloseToBuilding = currentHouseDist < 150;
      const isBad = ai.score < 4 || ai.anbefaling === 'FORBUDT' || isNoRoad;

      if (isBad) {
        let reason = "Ikke egnet for camping";
        if (isNoRoad) reason = "Ingen kjørbar vei funnet";
        else if (ai.anbefaling === 'FORBUDT') reason = "Vurdert som forbudt område";
        else if (ai.score < 4) reason = `Lav kvalitetsscore (${ai.score}/10)`;
        addToast?.(`Uegnet: ${reason}`, 'warning');
        return;
      }

      // For nær bebyggelse: godkjent, men score capper på 4 og advarsel vises
      const finalScore = tooCloseToBuilding ? 4 : Math.max(4, ai.score);
      const buildingWarning = tooCloseToBuilding
        ? `⚠️ For nær bebyggelse (${currentHouseDist}m – under 150m-grensen). `
        : '';

      const analyzedSpot: CampingSpot = {
        ...spotForAI,
        aiResult: ai,
        score: finalScore,
        description: buildingWarning + ai.vurdering,
        isInvalid: false,
        suitabilityColor: finalScore >= 8 ? BRAND : '#f59e0b',
        ...(Number.isFinite(ai.elevation_moh) ? { elevation: Math.round(ai.elevation_moh) } : {})
      };

      setSpots(prev => [...prev, analyzedSpot]);
      await upsertSpotsToFirebase([analyzedSpot]);
      onSelectSpot(analyzedSpot);

      if (tooCloseToBuilding) {
        addToast?.(`For nær bebyggelse (${currentHouseDist}m) – score satt til 4`, 'warning');
      } else {
        addToast?.("Egnet for camping!", 'success');
      }

      // FASE 4: Bakgrunnsberikelse (ikke-blokkerende)
      enrichSpotWithGeoData(analyzedSpot).then(async (enrichedSpot) => {
        setSpots(prev => prev.map(s => s.id === tempId ? enrichedSpot : s));
        await upsertSpotsToFirebase([enrichedSpot]);
      }).catch(err => {
        console.warn('[analyzeOneCoords] Bakgrunnsberikelse feilet:', err);
      });

    } catch (err: any) {
      console.error("Manuell analyse feilet:", err);
      const msg = err?.message || String(err);
      if (msg.includes('504') || msg.toLowerCase().includes('overpass') || msg.toLowerCase().includes('timeout')) {
        addToast?.("Analyse feilet: Overpass (kartdata) svarte ikke. Prøv igjen.", 'error');
      } else if (msg.includes('NVDB') || msg.includes('vegvesen')) {
        addToast?.("Analyse feilet: SVV/NVDB er utilgjengelig.", 'error');
      } else {
        addToast?.(`Analyse feilet: ${msg.slice(0, 80)}`, 'error');
      }
    }
  };

  const processAnalysisQueue = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    setIsManualAnalyzing(true);
    setIsOverlayMinimized(false);

    try {
      while (analysisQueueRef.current.length > 0) {
        const coords = analysisQueueRef.current.shift()!;
        // +1 so the overlay counts the item currently being analyzed, not just waiting items
        setQueueLength(analysisQueueRef.current.length + 1);
        await analyzeOneCoords(coords);
        setQueueLength(analysisQueueRef.current.length);
      }
    } finally {
      isProcessingRef.current = false;
      setIsManualAnalyzing(false);
      setQueueLength(0);
    }
  };

  const handleManualAnalysis = () => {
    if (!pendingAnalysisCoords) return;
    const alreadyRunning = isProcessingRef.current;
    analysisQueueRef.current.push(pendingAnalysisCoords);
    setQueueLength(analysisQueueRef.current.length + (alreadyRunning ? 1 : 0));
    setPendingAnalysisCoords(null);
    if (alreadyRunning) {
      addToast?.(`Punkt lagt til kø (${analysisQueueRef.current.length} venter)`, 'info');
    }
    processAnalysisQueue();
  };

  return {
    isManualAnalyzing, isViewportAnalyzing, analysisProgress, pendingAnalysisCoords, setPendingAnalysisCoords,
    activeAnalysisIds, setActiveAnalysisIds, isOverlayMinimized, setIsOverlayMinimized, queueLength,
    handleCancelScan, handleScanWithBg, handleDeepAnalyzeViewport, handleManualAnalysis, handleReevaluateViewport, handleMinimize
  };
};
